/**
 * One localStorage value per signed-in account on this device: the key
 * carries the account and its sign-in epoch, and the previous account's
 * value is removed when the auth scope changes (sign-out, another account).
 * Every access is guarded: storage may be unavailable. Subscribers hear
 * this window's writes and other windows' (the `storage` event).
 */
import { conversationBrowserAccountKey } from "@/hooks/conversation-browser-cache";
import { subscribeDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";

export interface AccountScopedStorage {
  /** The stored value for the current account, or null. */
  read: () => string | null;
  write: (value: string) => void;
  remove: () => void;
  /** Writes here or in another window, and account changes. */
  subscribe: (listener: () => void) => () => void;
}

export function accountScopedStorage(prefix: string): AccountScopedStorage {
  const changeEvent = `denker:account-storage:${prefix}`;
  const storageKey = () =>
    prefix + (conversationBrowserAccountKey() ?? "signed-out");
  let previousKey = storageKey();
  subscribeDesktopChatAuthScope(() => {
    const nextKey = storageKey();
    if (previousKey === nextKey) return;
    try {
      localStorage.removeItem(previousKey);
    } catch {
      /* Storage may be unavailable. */
    }
    previousKey = nextKey;
  });

  const changed = () => window.dispatchEvent(new Event(changeEvent));

  return {
    read: () => {
      try {
        return localStorage.getItem(storageKey());
      } catch {
        return null;
      }
    },
    write: (value) => {
      try {
        localStorage.setItem(storageKey(), value);
      } catch {
        return;
      }
      changed();
    },
    remove: () => {
      try {
        localStorage.removeItem(storageKey());
      } catch {
        return;
      }
      changed();
    },
    subscribe: (listener) => {
      const onStorage = (event: StorageEvent) => {
        if (event.key === storageKey() || event.key === null) listener();
      };
      const unsubscribeAuth = subscribeDesktopChatAuthScope(listener);
      window.addEventListener("storage", onStorage);
      window.addEventListener(changeEvent, listener);
      return () => {
        unsubscribeAuth();
        window.removeEventListener("storage", onStorage);
        window.removeEventListener(changeEvent, listener);
      };
    },
  };
}
